import { createContext, useContext, useMemo, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api, RESERVED_SLUGS, resolveHost, setResolvedTenant } from '../services/api';
import { resolveImgUrl } from '../templates/4/utils';

// Config vacía mientras carga la API — no hay hardcodeo por cliente
const EMPTY_CONFIG = {
  id: null,
  name: '',
  displayName: '',
  descripcion: null,
  carouselImages: null,
  templateCodigo: '1',
  theme: {},
  uiConfig: {},
  logo: {},
  favicon: null,
  loginBg: null,
  contact: {},
  social: {},
};

// Mapea la respuesta de la API al shape que usa el resto de la app
const mapApiConfig = (slug, data) => ({
  id: slug,
  name: data.nombre,
  displayName: data.display_name,
  descripcion: data.descripcion || null,
  carouselImages: data.carousel_images ? JSON.parse(data.carousel_images) : null,
  websiteUrl: data.website_url,
  templateId: data.template_id,
  templateCodigo: data.template_codigo,
  templateNombre: data.template_nombre,
  // Funciones opcionales prendidas desde /platform (ver tieneCapacidad).
  capacidades: data.capacidades || [],
  theme: {
    primaryColor: data.color_primary,
    secondaryColor: data.color_secondary,
    accentColor: data.color_accent,
    backgroundColor: data.color_background,
    textColor: data.color_text,
    primaryTextColor: data.color_primary_text,
  },
  uiConfig: data.ui_config ? JSON.parse(data.ui_config) : {},
  logo: {
    desktop: data.logo_desktop,
    mobile: data.logo_mobile,
    footer: data.logo_footer,
    dark: data.logo_dark || null,
    login: data.logo_login || null,
  },
  favicon: data.favicon,
  loginBg: data.login_bg || null,
  contact: {
    email: data.email,
    phone: data.telefono,
    whatsapp: data.whatsapp,
    address: data.domicilio,
  },
  social: {
    instagram: data.instagram,
    facebook: data.facebook,
    tiktok: data.tiktok,
    twitter: data.twitter,
  },
});

export const ClientContext = createContext();

export const ClientProvider = ({ children }) => {
  const location = useLocation();

  const { slugFromPath, isCustomDomain, customHostname } = useMemo(() => {
    const host = resolveHost();

    if (host.kind === 'subdomain') {
      return { slugFromPath: host.slug, isCustomDomain: false, customHostname: null };
    }
    if (host.kind === 'custom') {
      return { slugFromPath: null, isCustomDomain: true, customHostname: host.hostname };
    }
    if (host.kind === 'platform') {
      return { slugFromPath: null, isCustomDomain: false, customHostname: null };
    }

    const pathParts = location.pathname.split('/').filter(Boolean);
    const first = pathParts[0] || null;
    return { slugFromPath: first && RESERVED_SLUGS.includes(first) ? null : first, isCustomDomain: false, customHostname: null };
  }, [location.pathname]);

  const [clientId, setClientId] = useState(null);
  const [clientConfig, setClientConfig] = useState(EMPTY_CONFIG);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (isCustomDomain) {
      // Dominio custom: una sola llamada resuelve todo
      api.tienda.getConfigByDomain(customHostname)
        .then(data => {
          if (data && data.slug) {
            setResolvedTenant(data.slug); // sino el X-Tenant-Id va vacio
            setClientId(data.slug);
            setClientConfig(mapApiConfig(data.slug, data));
            setNotFound(false);
          } else {
            setNotFound(true);
          }
        })
        .catch(() => setNotFound(true))
        .finally(() => setLoadingConfig(false));
      return;
    }
    setClientId(slugFromPath);
  }, [isCustomDomain, customHostname, slugFromPath]);

  useEffect(() => {
    if (isCustomDomain) return;

    if (!clientId) {
      setLoadingConfig(false);
      setNotFound(false);
      return;
    }

    setLoadingConfig(true);
    setNotFound(false);
    setClientConfig(EMPTY_CONFIG);

    api.tienda.getConfig(clientId)
      .then(data => {
        if (data && data.slug) {
          setClientConfig(mapApiConfig(clientId, data));
        } else {
          setNotFound(true);
        }
      })
      .catch(() => {
        setNotFound(true);
      })
      .finally(() => setLoadingConfig(false));
  }, [clientId, isCustomDomain]);

  // Aplicar favicon dinámico
  useEffect(() => {
    if (clientConfig.favicon) {
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = resolveImgUrl(clientConfig.favicon);
      document.head.appendChild(link);
    }
  }, [clientConfig.favicon]);

  const refreshConfig = async () => {
    if (!clientId) return;
    const data = await api.tienda.getConfig(clientId);
    if (data && data.slug) setClientConfig(mapApiConfig(clientId, data));
  };

  // En localhost las rutas llevan el slug como prefijo (/techdrop/catalogo),
  // en producción el slug está en el subdominio y las rutas son simples (/catalogo).
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const clientPath = (path) => isLocalhost ? `/${clientId}${path}` : path;

  return (
    <ClientContext.Provider value={{
      clientId,
      clientPath,
      theme: clientConfig.theme,
      uiConfig: clientConfig.uiConfig,
      config: clientConfig,
      loadingConfig,
      notFound,
      templateId: clientConfig.templateId,
      templateCodigo: clientConfig.templateCodigo || '1',
      // El admin muestra una función opcional solo si está prendida. El backend
      // la rechaza igual: esto es para no mostrar botones que no andan.
      tieneCapacidad: (codigo) => (clientConfig.capacidades || []).includes(codigo),
      refreshConfig,
    }}>
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient debe usarse dentro de ClientProvider');
  }
  return context;
};
